import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";

const row = "flex min-h-[48px] items-center gap-3 border-b border-black/[0.08] px-4 last:border-0";
const lab = "w-24 shrink-0 text-[15px] text-black sm:w-32";
const fieldCls = "h-11 min-w-0 flex-1 truncate bg-transparent text-right text-[15px] text-[#3C3C43] outline-none placeholder:text-[#C7C7CC]";

function Group({ title, children, testId }) {
  return (
    <div className="space-y-1.5" data-testid={testId}>
      <p className="px-4 text-[13px] uppercase text-[#6D6D72]">{title}</p>
      <div className="overflow-hidden rounded-[12px] bg-white">{children}</div>
    </div>
  );
}

export function CustomerEditForm({ c, people, onSaved, onCancel }) {
  const [f, setF] = useState({ name: c.name || "", company: c.company || "", email: c.email || "", phone: c.phone || "", address: c.address || "",
    notes: c.notes || "", showroom_id: c.showroom_id || "", rep_id: c.rep_id || "", markup_preset_id: c.markup_preset_id || "" });
  const [presets, setPresets] = useState([]);
  const [dup, setDup] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (c.can_set_preset) api.get("/markup-presets").then((r) => setPresets(r.data)).catch(() => {}); }, [c.can_set_preset]);
  const set = (k) => (e) => { setF({ ...f, [k]: e.target.value }); if (k === "email" || k === "phone") setDup(""); };
  const save = async (confirm = false) => {
    if (!f.name.trim()) return toast.error("Customer name is required");
    setBusy(true);
    try {
      const { data } = await api.put(`/customers/${c.id}`, { ...f, showroom_id: f.showroom_id || null, rep_id: f.rep_id || null,
        markup_preset_id: f.markup_preset_id || null, confirm_duplicate: confirm });
      toast.success("Customer saved");
      onSaved(data);
    } catch (e) {
      if (e.response?.status === 409) setDup(errorMessage(e));
      else toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const text = (k, label, type = "text", ph = "") => (
    <label className={row}><span className={lab}>{label}</span>
      <input data-testid={`customer-edit-${k}-input`} type={type} value={f[k]} placeholder={ph} onChange={set(k)} className={fieldCls} /></label>
  );
  const reps = people.users.filter((u) => !c.can_move_showroom || !f.showroom_id || u.role === "admin" || u.showroom_id === f.showroom_id || u.id === f.rep_id);
  return (
    <div className="space-y-6 rounded-[14px] bg-[#F2F2F7] p-4 sm:p-6" data-testid="customer-edit-form">
      <div className="flex items-center justify-between">
        <button data-testid="customer-edit-cancel" onClick={onCancel} className="min-h-[44px] text-[17px] text-[#0062CC]">Cancel</button>
        <p className="text-[17px] font-semibold text-black">Edit Customer</p>
        <button data-testid="customer-edit-save" disabled={busy} onClick={() => save(false)} className="min-h-[44px] text-[17px] font-semibold text-[#0062CC] disabled:opacity-40">Save</button>
      </div>
      <Group title="Contact" testId="customer-edit-contact">
        {text("name", "Name", "text", "Required")}{text("company", "Company")}{text("email", "Email", "email")}{text("phone", "Phone", "tel")}{text("address", "Address")}
      </Group>
      {dup && (
        <div data-testid="customer-edit-duplicate-warning" className="rounded-[12px] bg-[#FF9500]/10 px-4 py-3 text-[15px] text-[#8A4B00]">
          {dup} <button data-testid="customer-edit-save-anyway" onClick={() => save(true)} className="ml-1 font-semibold text-[#0062CC]">Save anyway</button>
        </div>
      )}
      {c.can_assign && (
        <Group title="Assignment" testId="customer-edit-assignment">
          {c.can_move_showroom && (
            <label className={row}><span className={lab}>Showroom</span>
              <select data-testid="customer-edit-showroom-select" value={f.showroom_id} onChange={(e) => { const sid = e.target.value; const ok = (u) => u.role === "admin" || u.showroom_id === sid;
                const cur = people.users.find((u) => u.id === f.rep_id); setF({ ...f, showroom_id: sid, rep_id: cur && ok(cur) ? f.rep_id : (people.users.find(ok)?.id || "") }); }} className={`${fieldCls} cursor-pointer [text-align-last:right]`}>
                {people.showrooms.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select></label>
          )}
          <label className={row}><span className={lab}>Sales rep</span>
            <select data-testid="customer-edit-rep-select" value={f.rep_id} onChange={set("rep_id")} className={`${fieldCls} cursor-pointer [text-align-last:right]`}>
              {reps.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select></label>
          <label className={row}><span className={lab}>Markup preset</span>
            <select data-testid="customer-edit-preset-select" value={f.markup_preset_id} onChange={set("markup_preset_id")} className={`${fieldCls} cursor-pointer [text-align-last:right]`}>
              {presets.map((p) => <option key={p.id} value={p.id}>{p.name}{p.multiplier ? ` (×${p.multiplier})` : ""}</option>)}
            </select></label>
        </Group>
      )}
      <Group title="Notes">
        <textarea data-testid="customer-edit-notes-input" value={f.notes} onChange={set("notes")} rows={4} className="block w-full resize-y bg-transparent px-4 py-3 text-[15px] text-black outline-none" />
      </Group>
      <p className="px-4 text-[13px] text-[#6D6D72]">Name changes update Draft estimates. Sent and approved estimates keep the details they were sent with.</p>
    </div>
  );
}
